'use es6';

import PropTypes from 'prop-types';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { List } from 'immutable';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { SYSTEM_RULES, MANUAL_SPAM, MANUAL_BLOCK, MANUAL, PROVIDER_MARKED_SPAM } from 'conversations-thread-data/thread-details/public/constants';
import { getMostWeightedRules } from 'conversations-thread-data/thread-details/public/utils';
import { getDynamicNamespace } from 'conversations-infrastructure/i18n/getDynamicNamespace';
import { isStarted, isLoading } from 'conversations-async-data/async-data/operators/statusComparators';
import { getFilteredSource, getIsFiltered } from 'conversations-thread-data/thread-details/public/operators';
import { useThreadDetails } from '../../thread-details-feature/public/hooks/useThreadDetails';
import { trackEvent } from '../../usage-tracking/clients/usageTracker';
import { getClickedAlertBannerCta } from '../../constants/CommonEvents';
import { getData } from 'conversations-async-data/async-data/operators/getters';
import { getUpdatedConversation } from '../../usage-tracking/utils/threadInteraction';
import UndoButton from '../../alerts/components/UndoButton';
import SpamReason from './SpamReason';
import { UNMARK_AS_SPAM_MARKER } from '../../performance-metrics/constants/performanceConstants';
import { useFilterRules } from 'conversations-thread-data/thread-details/public/hooks';
import { getAllowDenyListSettingsUrl } from '../../urls/getAllowDenyListSettingsUrl';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const spamThreadAlerts = [SYSTEM_RULES, MANUAL_SPAM, MANUAL_BLOCK, MANUAL, PROVIDER_MARKED_SPAM];
export const SpamThreadAlertButton = ({
  loading = false,
  filterType,
  onClickHandler
}) => {
  // in case of PROVIDER_MARKED_SPAM on click, we want to send the user to
  // the allow and deny settings page
  return /*#__PURE__*/_jsx(LoadingButton, {
    preventClicksOnLoading: true,
    size: "extra-small",
    use: "tertiary",
    loading: loading,
    onClick: filterType !== PROVIDER_MARKED_SPAM ? onClickHandler : undefined,
    href: filterType === PROVIDER_MARKED_SPAM ? getAllowDenyListSettingsUrl() : undefined,
    target: filterType === PROVIDER_MARKED_SPAM ? 'blank' : undefined,
    marginInlineStart: "300",
    children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
      message: getDynamicNamespace({
        translationOptions: spamThreadAlerts,
        default: 'MANUAL',
        prefix: 'conversations-thread-view.spam-filtering-and-blocking.FilterThreadAlert.button'
      }, filterType)
    })
  });
};
SpamThreadAlertButton.displayName = 'SpamThreadAlertButton';
SpamThreadAlertButton.propTypes = {
  loading: PropTypes.bool,
  filterType: PropTypes.string,
  onClickHandler: PropTypes.func
};
const SpamThreadAlert = ({
  threadId
}) => {
  const {
    asyncFilterChangeRequest,
    asyncThreadDetails,
    blockThread,
    markAsSpam,
    unmarkAsSpam,
    unblockThread
  } = useThreadDetails({
    threadId
  });
  const threadDetails = getData(asyncThreadDetails);
  const filterSource = getFilteredSource(threadDetails);
  const {
    asyncFilterRules
  } = useFilterRules({
    threadId
  });
  const triggeredSpamRules = getData(asyncFilterRules);
  let highestTriggeredSpamRules = List([]);
  let buttonFilterSource = filterSource;
  if (!threadDetails || !getIsFiltered(threadDetails)) {
    return null;
  }
  let alertType = 'warning';
  if (filterSource === SYSTEM_RULES && triggeredSpamRules.size > 0) {
    highestTriggeredSpamRules = getMostWeightedRules(triggeredSpamRules);
    if (highestTriggeredSpamRules.contains(PROVIDER_MARKED_SPAM)) {
      // for PROVIDER_MARKED_SPAM we show a different background and
      // different label for the button compared to other SYSTEM_RULES
      alertType = 'danger';
      buttonFilterSource = PROVIDER_MARKED_SPAM;
    }
  }
  const handleButtonClicked = () => {
    const ctaAction = `unmark thread as ${filterSource.toLowerCase()}`;
    trackEvent(getClickedAlertBannerCta({
      ctaAction
    }));

    /**
     * If the filterSource is MANUAL_BLOCK or MANUAL, unblock the thread
     *
     * Otherwise, the filterSource will be one of [SYSTEM_RULES, MANUAL_SPAM]
     *  and the thread should be unmarked as spam
     */
    if (filterSource === MANUAL_BLOCK || filterSource === MANUAL) {
      unblockThread();
      FloatingAlertStore.addAlert({
        message: /*#__PURE__*/_jsxs("div", {
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: "conversations-thread-view.alerts.unblocked"
          }), /*#__PURE__*/_jsx(UndoButton, {
            onClick: blockThread
          })]
        }),
        titleText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "conversations-thread-view.alerts.titles.success"
        }),
        type: 'success'
      });
      trackEvent(getUpdatedConversation({
        updateType: 'unblock'
      }));
    } else {
      performance.mark(`${UNMARK_AS_SPAM_MARKER}-start`);
      unmarkAsSpam();
      FloatingAlertStore.addAlert({
        message: /*#__PURE__*/_jsxs("div", {
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: "conversations-thread-view.alerts.unmarked-as-spam"
          }), /*#__PURE__*/_jsx(UndoButton, {
            onClick: markAsSpam
          })]
        }),
        titleText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "conversations-thread-view.alerts.titles.success"
        }),
        type: 'success'
      });
      trackEvent(getUpdatedConversation({
        updateType: 'unmark spam'
      }));
    }
  };
  return /*#__PURE__*/_jsxs(Alert, {
    type: alertType,
    children: [/*#__PURE__*/_jsx(SpamReason, {
      source: filterSource,
      triggeredSpamRules: highestTriggeredSpamRules,
      isLoading: isLoading(asyncFilterRules)
    }), /*#__PURE__*/_jsx(SpamThreadAlertButton, {
      loading: isStarted(asyncFilterChangeRequest),
      filterType: buttonFilterSource,
      onClickHandler: handleButtonClicked
    })]
  });
};
SpamThreadAlert.displayName = 'FilteredThreadAlert';
SpamThreadAlert.propTypes = {
  threadId: PropTypes.string.isRequired
};
export default SpamThreadAlert;